<template>
  <v-row no-gutters>
    <v-col v-if="$slots.col" cols="6">
      <slot name="col"></slot>
    </v-col>

    <v-col v-if="!disabled" :cols="$slots.col ? 6 : 12" :class="[{'disabled': disabled}]">
      <div class="my-0 _subtitleFormFont">Preenchimento:</div>
      <div class="options-container">
        <div 
          v-for="(opt, index) in showAvailableTypes" 
          :key="index" 
          :class="['options', {'option-selected': isOptSelected(opt)}]"
          @click="handleChangeType(opt.type)"
        >
          <v-icon size="19" :color="isOptSelected(opt) ? 'bt_color' : ''">
            {{opt.icon}}
          </v-icon>
        </div>
      </div>
    </v-col>

    <v-col v-if="!disabled" cols="12" :class="['pt-4', {'disabled': disabled}]">
      <ColorPickerV2
        v-if="bgSelector['background-type'] === 'color'"
        :disabled="disabled"
        :colorSelected="bgSelector" 
        type="background-color" 
        title="Cor de fundo" 
      />

      <div v-if="bgSelector['background-type'] === 'image'">
        <div class="my-0 _subtitleFormFont">Imagem de fundo:</div>
        <div v-if="!$slots.image">
          <div v-if="!bgSelector['background-image']" class="custom-img-btn">
            <input
              ref="uploader"
              class="d-none"
              type="file"
              name="photo"
              accept="image/*"
              @change="onFileChanged"
            />            
            <Button 
              label="Upload de imagem" 
              color="bt_gray_bg"
              textColor="darkBlue"
              textSize="0.80rem"
              icon="fa-solid fa-upload"
              @click="handleUpload"
            />
          </div>

          <div v-else class="custom-img">
            <div class="thumb" :style="`background-image: url(${bgSelector['background-image']})`"></div>
            <div class="url">{{getImageName(bgSelector['background-image'])}}</div>
            <div class="delete">
              <div class="icon" @click="handleDeleteImage">
                <v-icon size="14">fa-regular fa-trash</v-icon>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <slot name="image"></slot>
        </div>
      </div>

      <TextField 
        v-if="bgSelector['background-type'] === 'video'" 
        :disabled="disabled"
        label="Video de fundo"
        placeholder="https://enderecodovideo.com"
        :hideDetails="false"
        :rules="[rules.isValidHttps]"
        v-model="value['background-video']"
        @input="() => { $forceUpdate() }"
      />
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex'
import ColorPickerV2 from '../ui/ColorPickerV2.vue'
import TextField from '../ui/TextField.vue'
import Button from '../ui/Button.vue'

export default {
  name: 'BgSelector',
  components: {ColorPickerV2, TextField, Button},
  props: {
    value: {type: Object}, // object needs to have properties: {background-color, background-image, background-video, background-type}
    availableTypes: {type: Array, default: () => ['color', 'image', 'video']},
    disabled: {type: Boolean}
  },

  data: () => ({
    types: [
      {type: 'image', icon: 'fa-light fa-image'},
      {type: 'video', icon: 'fa-light fa-clapperboard-play'},
      {type: 'color', icon: 'fa-light fa-brush'}
    ],
    rules: {
      isValidHttps: (v) => {
        const mediaUrlRegex = /^(https?:\/\/.*\.(mp4|avi|mov|mkv|flv|wmv|webm|ogg|m4a|aac)(\?.*)?)$/i;
        return mediaUrlRegex.test(v) || "Endereço de video inválido"
      } 
    }
  }),

  computed: {
    ...mapGetters(['infos']),
    bgSelector: {
      get () { return this.value },
      set (v) { 
        this.$emit('input', v)
      }      
    },

    showAvailableTypes () {
      return this.types.filter(type => this.availableTypes.includes(type.type))
    },
  },

  methods: {
    isOptSelected (opt) {
      return this.bgSelector['background-type'] === opt.type
    },

    handleChangeType (type) {
      this.bgSelector = {...this.bgSelector, 'background-type': type}
    },

    getImageName (url) {
      return url.split('/').pop() || ''
    },

    handleUpload () {
      this.$refs.uploader.click()
    },

    handleDeleteImage () {
      this.bgSelector = {...this.bgSelector, 'background-image': ''}
    },

    async onFileChanged(event) {
      const { files } = event.target
      const formData = new FormData();
      formData.append("media", files[0]);
      formData.append("hotsiteId", this.infos["organizer-id"]);

      const uploadedImg = await this.$store.dispatch("setImage", {
        formData: formData,
        token: this.$route.params.token,
      });

      if (uploadedImg.url) this.bgSelector = {...this.bgSelector, 'background-image': uploadedImg.url}
    },    
  }
}
</script>

<style scoped>
.disabled {
  pointer-events: none;
  opacity: 0.7;
}

.options-container {
  display: flex;
  gap: 8px
}

.options-container .options {
  display: flex;
  justify-content: center;
  align-items: center;  
  height: 40px !important;
  width: 40px !important;
  border-radius: 8px;
  background-color: #F6F7F7;
  border: 1px solid #EDEDF1; 
  cursor: pointer;
}

.option-selected {
  background-color: #E1F2FEB2 !important;
  border-color: #0067AF80 !important;
}

.custom-img {
  display: flex;
  align-items: center;
  gap: 8px
}

.custom-img-btn button {
  width: 100% !important;
  transition: all 0.3s
}

.custom-img-btn button:hover {
  background-color: #CBCBCE !important;
}

.custom-img .thumb {
  min-width: 40px;
  min-height: 40px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.custom-img .url {
  flex: 1;
  overflow: hidden;
  direction: rtl; 
}

.custom-img .delete {
  display: flex;
  justify-content: end;
  min-width: 50px;
}

.delete .icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background-color: #F6F7F7;
  cursor: pointer;
  transition: all 0.3s;
}

.delete .icon:hover {
  background-color: #CBCBCE;
}
</style>